import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError, switchMap, mergeMap, withLatestFrom } from 'rxjs/operators';
import { catchComplete } from 'app/utils';
import { ApiService } from 'store/api/api.service';
import * as MeasurementActions from 'store/measurements-store/measurements.actions';
import * as DocumentActions from './document.actions';
import * as fromRouterSelectors from 'store/router-store/router.selectors';
import * as fromDocuments from './document.reducer';
import dayjs from 'dayjs';

@Injectable()
export class DocumentEffects {
  constructor(
    private actions$: Actions,
    private store: Store<fromDocuments.State | fromRouterSelectors.State>,
    private apiService: ApiService
  ) {}

  loadDocumentsPages$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DocumentActions.loadDocumentsPages),
      withLatestFrom(
        this.store.pipe(select(fromDocuments.selectPagesByCategory)),
        this.store.pipe(select(fromRouterSelectors.selectQueryParam('category')))
      ),
      mergeMap(([action, pages, category]) => {
        const categoryKey = Array.isArray(category) ? category[0] : category;

        return this.apiService
          .getDocuments(
            {
              categories: [categoryKey],
              page: pages?.page < pages?.totalPages ? pages.page + 1 : 1,
              page_size: 25,
            },
            {
              cache: true,
            }
          )
          .pipe(
            map((data) =>
              DocumentActions.loadDocumentsPagesSuccess({
                documents: data.results,
                pages: {
                  page:
                    pages === undefined || pages?.page < pages?.totalPages ? data.page : pages.page,
                  pageSize: data.page_size,
                  totalPages: data.total_pages,
                },
                category: categoryKey,
              })
            ),
            catchError((error) => of(DocumentActions.loadDocumentsFailure({ error }))),
            catchComplete(() => of(DocumentActions.loadDocumentsComplete({ action })))
          );
      })
    );
  });

  loadDocuments$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DocumentActions.loadDocuments),
      mergeMap((action) => {
        const requestParams = action.from
          ? {
              from: action.from,
              to: action.to || dayjs().format('YYYY-MM-DD'),
            }
          : undefined;

        const contextSettings = {
          cache: action.cache === false ? false : !!action.from,
          fetchOnce: !!action.from,
        };

        return this.apiService.getDocuments(requestParams, contextSettings).pipe(
          map((data) => DocumentActions.loadDocumentsSuccess({ documents: data.results })),
          catchError((error) => of(DocumentActions.loadDocumentsFailure({ error }))),
          catchComplete(() => of(DocumentActions.loadDocumentsComplete({ action })))
        );
      })
    );
  });

  loadDocumentsAfterAddMeasurements$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MeasurementActions.addMeasurementsSuccess),
      mergeMap((action) => {
        const today = dayjs().format('YYYY-MM-DD');

        return this.apiService
          .getDocuments({ from: today, to: today }, { cache: false, fetchOnce: false })
          .pipe(
            map((data) => DocumentActions.loadDocumentsSuccess({ documents: data.results })),
            catchError((error) => of(DocumentActions.loadDocumentsFailure({ error }))),
            catchComplete(() => of(DocumentActions.loadDocumentsComplete({ action })))
          );
      })
    );
  });

  loadDocumentsByPage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DocumentActions.loadDocumentsByPage),
      map((action) => action),
      mergeMap((action) =>
        this.apiService
          .getDocuments(
            {
              from: action.from,
              to: action.to,
              page: action.page,
              page_size: action.pageSize,
            },
            {
              cache: action.cache,
              fetchOnce: false,
            }
          )
          .pipe(
            map((data) => DocumentActions.loadDocumentsSuccess({ documents: data.results })),
            catchError((error) => of(DocumentActions.loadDocumentsFailure({ error }))),
            catchComplete(() => of(DocumentActions.loadDocumentsComplete({ action })))
          )
      )
    );
  });

  loadDocument$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DocumentActions.loadDocument),
      switchMap((action) =>
        this.apiService.getDocument(action.id).pipe(
          map((data) => DocumentActions.loadDocumentSuccess({ document: data })),
          catchError((error) => of(DocumentActions.loadDocumentFailure({ error })))
        )
      )
    );
  });

  loadDocumentByRouteId$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DocumentActions.loadDocumentByRouteId),
      withLatestFrom(this.store.pipe(select(fromRouterSelectors.selectRouteParam('id')))),
      map(([, routeId]) => DocumentActions.loadDocument({ id: +routeId }))
    );
  });

  deleteDocument$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DocumentActions.deleteDocument, DocumentActions.deleteDocumentFromDatabase),
      mergeMap((action) =>
        this.apiService.deleteDocument(action.id).pipe(
          map(() =>
            action.type === DocumentActions.deleteDocument.type
              ? DocumentActions.deleteDocumentSuccess({ id: action.id })
              : DocumentActions.deleteDocumentFromDatabaseSuccess()
          ),
          catchError((error) => of(DocumentActions.deleteDocumentFailure({ error })))
        )
      )
    );
  });
}
